/**
 * Transactions of the inspector (W2-06; ARCHITECTURE.md §5.3, DESIGN.md §4.11).
 *
 *  - `runTx`: one undoable transaction through `DocStore.transact`; a refused command shows its reason in a toast,
 *    notes of the command (split exactness, removed GIF data...) show as information toasts.
 *  - Coalescing per focus (`useEditKit`): every field row has its own focus session; its edits carry the coalesce
 *    key `field:<entity>:<field>:<session>`, so a typing burst (or a run of arrow steps) in one field becomes one
 *    undo step, and leaving the row seals the run (`sealHistory` on the extended DocStore) so the next edit starts
 *    a new entry even within the coalescing window.
 *  - Live scrubbing through a Gesture (label drag, wheel, arrow keys): ops apply immediately, one Txn on release,
 *    Escape restores (the field calls the scrub with the start value and `final`).
 *  - Playhead helpers: the playhead is not React state; commands read the transport when they run.
 */

import { useEffect, useRef, type FocusEvent } from 'react';
import type { AppServices } from '../../app/registry/types';
import { Beats } from '../../core/time/beat';
import type { Beat, BeatParseContext, Sec, TempoMap } from '../../core/time/types';
import type { MessageRef } from '../../core/util/types';
import type { TxBuilder } from '../../core/ops/types';
import { buildTempoMap } from '../../core/doc/container';
import type { DocStore, Gesture } from '../../state/types';

/** Command id recorded on inspector transactions (history icon, diagnostics). */
export const INSPECTOR_TXN_COMMAND = 'inspector.edit';

export interface TxOptions {
  readonly label: MessageRef;
  readonly coalesceKey?: string | undefined;
  readonly command?: string | undefined;
}

export type TxResult<R> = { readonly ok: true; readonly value: R } | { readonly ok: false };

/** Runs one undoable transaction; failures and notes reach the user as toasts. */
export function runTx<R>(
  services: AppServices,
  store: DocStore,
  opts: TxOptions,
  build: (tx: TxBuilder) => R,
): TxResult<R> {
  const out = store.transact(
    {
      label: opts.label,
      command: opts.command ?? INSPECTOR_TXN_COMMAND,
      ...(opts.coalesceKey === undefined ? {} : { coalesceKey: opts.coalesceKey }),
    },
    build,
  );
  if (!out.ok) {
    services.ui.toast(out.reason, { kind: 'error' });
    return { ok: false };
  }
  for (const note of out.notes) services.ui.toast(note, { kind: 'info' });
  return { ok: true, value: out.value };
}

let sessionSeq = 0;

/** Focus handlers of one field row. */
export interface RowFocusProps {
  readonly onFocus: (e: FocusEvent<HTMLElement>) => void;
  readonly onBlur: (e: FocusEvent<HTMLElement>) => void;
}

/** Edits of one inspected entity (a note set, a key set, a line...). */
export interface EditKit {
  /** Focus handlers for the row of `field` (put them on the element wrapping the field's controls). */
  row(field: string): RowFocusProps;
  /** Coalesce key of `field` for the current focus session (call from event handlers only). */
  key(field: string): string;
  /** One transaction, coalesced with the previous edit of the same field in the same focus session. */
  commit<R>(field: string, label: MessageRef, build: (tx: TxBuilder) => R): TxResult<R>;
  /** A transaction that never coalesces (buttons, menus). */
  once<R>(label: MessageRef, build: (tx: TxBuilder) => R, command?: string): TxResult<R>;
  /** Live scrubbing of `field`: every call replaces the pending ops, `final` commits one Txn. */
  scrub(field: string, label: MessageRef, build: (tx: TxBuilder) => void, final: boolean): void;
}

/**
 * The edit kit of an inspector section. `entity` identifies what is edited (e.g. `notes:12,13`), so two selections
 * never share a coalescing run. Sessions start when focus enters a row and end (sealing the history) when it leaves.
 */
export function useEditKit(services: AppServices, store: DocStore, entity: string): EditKit {
  const sessions = useRef<Map<string, number> | null>(null);
  const gesture = useRef<Gesture | null>(null);
  useEffect(
    () => () => {
      if (gesture.current?.active === true) gesture.current.cancel();
      gesture.current = null;
    },
    [],
  );
  const sessionsMap = (): Map<string, number> => {
    if (sessions.current === null) sessions.current = new Map();
    return sessions.current;
  };
  const key = (field: string): string => {
    const map = sessionsMap();
    let n = map.get(field);
    if (n === undefined) {
      n = ++sessionSeq;
      map.set(field, n);
    }
    return `field:${entity}:${field}:${String(n)}`;
  };
  return {
    row: (field) => ({
      onFocus: (e) => {
        const from = e.relatedTarget;
        if (from instanceof Node && e.currentTarget.contains(from)) return;
        sessionsMap().set(field, ++sessionSeq);
      },
      onBlur: (e) => {
        const to = e.relatedTarget;
        if (to instanceof Node && e.currentTarget.contains(to)) return;
        // Leaving the field ends its coalescing run (the commit made by the blur itself already happened).
        store.sealHistory();
        sessionsMap().set(field, ++sessionSeq);
      },
    }),
    key,
    commit: (field, label, build) => runTx(services, store, { label, coalesceKey: key(field) }, build),
    once: (label, build, command) => runTx(services, store, { label, command }, build),
    scrub: (field, label, build, final) => {
      let g = gesture.current;
      if (g?.active !== true) {
        g = store.beginGesture({ label, command: INSPECTOR_TXN_COMMAND, coalesceKey: key(field) });
        gesture.current = g;
      }
      const out = g.update(build);
      if (!out.ok) {
        g.cancel();
        gesture.current = null;
        services.ui.toast(out.reason, { kind: 'error' });
        return;
      }
      if (final) {
        g.commit();
        gesture.current = null;
        for (const note of out.notes) services.ui.toast(note, { kind: 'info' });
      }
    },
  };
}

/** Tempo map of the store's current document. */
export function tempoOf(store: DocStore): TempoMap {
  return buildTempoMap(store.doc, store.idx);
}

/** Chart time of the playhead (seconds). */
export function playheadSec(services: AppServices): Sec {
  const t = services.audio.transport.chartTime();
  return Number.isFinite(t) && t > 0 ? t : 0;
}

/**
 * Beat of the playhead, snapped to the beat grid when snapping is on (keyframes written at the playhead land on the
 * grid, D§5.6.6), else to 1/1920 beat (rationals stay small).
 */
export function playheadBeat(services: AppServices, tempo: TempoMap): Beat {
  const snap = services.editor.getState().snap;
  const div = snap.beatSnap ? Math.max(1, Math.min(1920, Math.round(snap.div))) : 1920;
  const b = Beats.snap(tempo.beatAt(playheadSec(services), 1920), div);
  return Beats.cmp(b, Beats.ZERO) < 0 ? Beats.ZERO : b;
}

/** One snap step (minimum hold length when converting to a Hold, D§5.3). */
export function snapStep(services: AppServices): Beat {
  const div = Math.max(1, Math.min(1920, Math.round(services.editor.getState().snap.div)));
  return Beats.make(0, 1, div);
}

/** Beat parse context for beat fields (snap, syntax setting, tempo, markers). */
export function beatContext(services: AppServices, store: DocStore, tempo: TempoMap): BeatParseContext {
  const markers = store.doc.markers;
  return {
    snapDiv: Math.max(1, Math.round(services.editor.getState().snap.div)),
    syntax: services.settings.value.editing.beatSyntax,
    tempo,
    marker: (name) => {
      const wanted = name.trim().toLowerCase();
      for (const mk of markers.values()) if (mk.name.trim().toLowerCase() === wanted) return mk.t;
      return undefined;
    },
  };
}

/** A fresh seed for `rand(...)` in expressions (shown to the user so the result can be reproduced). */
export function newSeed(): number {
  const buf = new Uint32Array(1);
  crypto.getRandomValues(buf);
  return (buf[0] ?? 1) % 1_000_000;
}
